import { API_CONFIG } from '../../config/api.config';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, tap, throwError, filter } from 'rxjs';
import { User, AuthResponse, RegisterRequest, LoginRequest, AuthGoogleResponse } from '../../models/user.model';
import { UtilityService } from '../utility/utility.service';
import { ThemeService } from '../theme/theme.service';

/**
 * Service to handle authentication operations.
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private _utilityService = inject(UtilityService);
  private _themeService = inject(ThemeService);

  private _currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser = this._currentUserSubject.asObservable();
  public userRole = this.currentUser.pipe(map(user => user?.role));

  private _isLoggedInSubject = new BehaviorSubject<boolean | null>(null);
  public isLoggedIn = this._isLoggedInSubject.asObservable().pipe(
    filter(val => val !== null),
    map(val => val as boolean)
  );

  private _tokenExpirationTimer: any;
  private _refreshTokenTimer: any;

  constructor() {
    this._loadStoredAuth();
  }

  /**
   * Registers a new user.
   * @param {RegisterRequest} data Registration data.
   * @returns {Observable<User>} The registered user.
   */
  register(data: RegisterRequest): Observable<User> {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.modules.auth.base}${API_CONFIG.modules.auth.endpoints.register}`;
    return this._utilityService.request<User>('POST', url, data, {}, false);
  }

  /**
   * Logs in with Google credentials.
   * @param {string} credential Google token.
   * @returns {Observable<AuthGoogleResponse>} Google auth response.
   */
  loginWithGoogle(credential: string): Observable<AuthGoogleResponse> {
    const url = `${API_CONFIG.baseUrl}${API_CONFIG.modules.auth.base}${API_CONFIG.modules.auth.endpoints.google}`;
    const data = { token: credential };

    return this._utilityService.request<AuthGoogleResponse>('POST', url, data, {}, false, { 'x-skip-loader': 'true' }).pipe(
      tap((response: AuthGoogleResponse) => {
        if (response.status !== 'incomplete') {
          this.handleLoginSuccess(response);
        }
      })
    );
  }

  /**
   * Logs in with username and password.
   * @param {LoginRequest} data Login credentials.
   * @returns {Observable<AuthResponse>} Auth response.
   */
  login(data: LoginRequest): Observable<AuthResponse> {
    const formData = new FormData();
    formData.append('username', data.username);
    formData.append('password', data.password);

    const url = `${API_CONFIG.baseUrl}${API_CONFIG.modules.auth.base}${API_CONFIG.modules.auth.endpoints.login}`;

    return this._utilityService.request<AuthResponse>('POST', url, formData, {}, false).pipe(
      tap((response: AuthResponse) => {
        this.handleLoginSuccess(response);
      })
    );
  }

  /**
   * Handles successful login response and initiates session.
   * @param {any} loginResponse Response from login API.
   */
  handleLoginSuccess(loginResponse: any): void {
    const expirationTime = this.calculateExpirationTime(loginResponse.access_token, loginResponse.expires_in);

    localStorage.setItem('access_token', loginResponse.access_token);
    localStorage.setItem('token_type', loginResponse.token_type);

    if (loginResponse.refresh_token) {
      localStorage.setItem('refresh_token', loginResponse.refresh_token);
    }

    localStorage.setItem('token_expiration', expirationTime.toString());
    const user = this._normalizeUser(loginResponse.user);
    localStorage.setItem('user_data', JSON.stringify(user));

    if (user.theme) {
      this._themeService.setTheme(user.theme);
    }

    this._currentUserSubject.next(user);
    this._isLoggedInSubject.next(true);

    this.setupTokenTimers(expirationTime);
  }

  /**
   * Gets the current access token.
   * @returns {string | null} Access token or null.
   */
  getAccessToken(): string | null {
    return localStorage.getItem('access_token');
  }

  /**
   * Gets the token type.
   * @returns {string | null} Token type or null.
   */
  getTokenType(): string | null {
    return localStorage.getItem('token_type') || 'bearer';
  }

  /**
   * Gets the full Authorization header value.
   * @returns {string | null} Authorization header value or null.
   */
  getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    const tokenType = this.getTokenType();

    if (token && tokenType) {
      return `${tokenType} ${token}`;
    }
    return null;
  }

  /**
   * Gets the refresh token.
   * @returns {string | null} Refresh token or null.
   */
  getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  /**
   * Refreshes the access token using the refresh token.
   * @returns {Observable<AuthResponse>} Auth response with new tokens.
   */
  refreshToken(): Observable<AuthResponse> {
    const refreshToken = this.getRefreshToken();

    if (!refreshToken) {
      return throwError(() => new Error('No refresh token available'));
    }

    const url = `${API_CONFIG.baseUrl}${API_CONFIG.modules.auth.base}${API_CONFIG.modules.auth.endpoints.refresh}`;
    const body = { refresh_token: refreshToken };

    return this._utilityService.request<AuthResponse>('POST', url, body, {}, false, { 'x-skip-loader': 'true' }).pipe(
      tap((response: AuthResponse) => {
        localStorage.setItem('access_token', response.access_token);
        localStorage.setItem('token_type', response.token_type);

        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token);
        }

        const expirationTime = this.calculateExpirationTime(response.access_token);
        localStorage.setItem('token_expiration', expirationTime.toString());
        this.setupTokenTimers(expirationTime);
      }),
      catchError((error) => {
        console.error('Error refreshing token:', error);
        this.logout();
        return throwError(() => error);
      })
    );
  }

  /**
   * Checks if the current token is valid (not expired).
   * @returns {boolean} True if valid.
   */
  isTokenValid(): boolean {
    const expirationTime = localStorage.getItem('token_expiration');
    if (!expirationTime) {
      return false;
    }

    const expiration = parseInt(expirationTime, 10);
    const now = Date.now();

    return now < (expiration - 5 * 60 * 1000);
  }

  /**
   * Checks if the user is currently authenticated.
   * @returns {boolean} True if authenticated.
   */
  isAuthenticated(): boolean {
    return this._isLoggedInSubject.value === true && this.isTokenValid();
  }

  /**
   * Gets the current user object.
   * @returns {User | null} Current user.
   */
  getCurrentUser(): User | null {
    return this._currentUserSubject.value;
  }

  /**
   * Gets the full name of the current user.
   * @returns {string} Full name or 'Invitado'.
   */
  getUserFullname(): string {
    return this._currentUserSubject.value?.first_name + ' ' + this._currentUserSubject.value?.last_name || 'Invitado';
  }

  /**
   * Gets the first name of the current user.
   * @returns {string} First name or 'Invitado'.
   */
  getUserName(): string {
    return this._currentUserSubject.value?.first_name || 'Invitado';
  }

  /**
   * Logs out the user and clears session data.
   */
  logout(): void {
    if (this._tokenExpirationTimer) {
      clearTimeout(this._tokenExpirationTimer);
    }
    if (this._refreshTokenTimer) {
      clearTimeout(this._refreshTokenTimer);
    }

    localStorage.removeItem('access_token');
    localStorage.removeItem('token_type');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('token_expiration');

    this._currentUserSubject.next(null);
    this._isLoggedInSubject.next(false);
  }

  /**
   * Updates partial user data locally.
   * @param {Partial<User>} userData Data to update.
   * @returns {Observable<User>} The updated user.
   */
  updateUserData(userData: Partial<User>): Observable<User> {
    return this._utilityService.request<User>('PUT', `${API_CONFIG.baseUrl}${API_CONFIG.modules.user.base}${API_CONFIG.modules.user.endpoints.update}`, userData).pipe(
      tap((updatedUser: User) => {
        localStorage.setItem('user_data', JSON.stringify(updatedUser));
        this._currentUserSubject.next(updatedUser);
      }),
      catchError((error) => {
        console.error('Error updating user data:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Loads stored authentication data from local storage.
   */
  private _loadStoredAuth(): void {
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user_data');
    const expirationTime = localStorage.getItem('token_expiration');

    if (token && userData && this.isTokenValid()) {
      try {
        const user: User = this._normalizeUser(JSON.parse(userData));

        if (user.theme) {
          this._themeService.setTheme(user.theme);
        }

        this._currentUserSubject.next(user);
        this._isLoggedInSubject.next(true);

        if (expirationTime) {
          this.setupTokenTimers(parseInt(expirationTime, 10));
        }

      } catch (error) {
        console.error('Error parsing stored user data:', error);
        this.logout();
      }
    } else {
      if (this.getRefreshToken() && userData) {
        this.refreshToken().subscribe({
          next: () => {
            try {
              const user: User = this._normalizeUser(JSON.parse(userData));

              if (user.theme) {
                this._themeService.setTheme(user.theme);
              }

              this._currentUserSubject.next(user);
              this._isLoggedInSubject.next(true);
            } catch (error) {
              console.error('Error parsing stored user data:', error);
              this.logout();
            }
          },
          error: () => {
            console.log('Failed to refresh token on load, user must login again');
            this.logout();
          }
        });
      } else {
        this._isLoggedInSubject.next(false);
      }
    }
  }

  /**
   * Calculates token expiration time.
   * @param {string} token JWT token.
   * @param {number} [expiresIn] Optional explicit expiration in seconds.
   * @returns {number} Expiration timestamp.
   */
  private calculateExpirationTime(token: string, expiresIn?: number): number {
    if (expiresIn) {
      return Date.now() + (expiresIn * 1000);
    }

    try {
      const payload = this.decodeJWT(token);
      if (payload.exp) {
        return payload.exp * 1000;
      }
    } catch (error) {
      console.error('Error decoding JWT:', error);
    }

    return Date.now() + (60 * 60 * 1000);
  }

  /**
   * Decodes a JWT token without verification.
   * @param {string} token JWT string.
   * @returns {any} Decoded payload.
   */
  private decodeJWT(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  }

  /**
   * Sets up timers for token refresh and expiration.
   * @param {number} expirationTime Expiration timestamp.
   */
  private setupTokenTimers(expirationTime: number): void {
    const now = Date.now();
    const timeUntilExpiration = expirationTime - now;
    const timeUntilRefresh = timeUntilExpiration - (10 * 60 * 1000); // Refresh 10 min before

    if (this._tokenExpirationTimer) {
      clearTimeout(this._tokenExpirationTimer);
    }
    if (this._refreshTokenTimer) {
      clearTimeout(this._refreshTokenTimer);
    }

    if (timeUntilRefresh > 0) {
      this._refreshTokenTimer = setTimeout(() => {
        console.log('Attempting automatic token refresh...');
        this.refreshToken().subscribe({
          next: () => console.log('Token refreshed successfully'),
          error: (err) => console.error('Failed to refresh token:', err)
        });
      }, timeUntilRefresh);
    }

    if (timeUntilExpiration > 0) {
      this._tokenExpirationTimer = setTimeout(() => {
        this.logout();
      }, timeUntilExpiration);
    }
  }

  /** Normalizes the raw API user object: maps `_id` → `id` when `id` is absent (Pydantic alias). */
  private _normalizeUser(raw: any): User {
    return { ...raw, id: raw.id ?? raw._id };
  }
}
